import { useEffect, useRef, useState } from "react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { api, formatApiError, CURRENCY_SYMBOLS } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Upload, Save } from "lucide-react";
import { toast } from "sonner";
import { ImageCropModal, fileToDataUrl } from "@/components/ImageCropModal";

export default function RestaurantProfile() {
  const [r, setR] = useState(null);
  const [saving, setSaving] = useState(false);
  const logoRef = useRef(null);
  const coverRef = useRef(null);
  // Image-crop state: `target` is "logo" | "cover", `src` is the raw uploaded
  // data URL, and the modal opens whenever both are set.
  const [crop, setCrop] = useState({ target: null, src: null });

  useEffect(() => {
    api.get("/restaurant").then((x) => setR(x.data)).catch((e) => toast.error(formatApiError(e)));
  }, []);

  if (!r) return <DashboardLayout role="owner"><div className="text-muted-foreground">Loading…</div></DashboardLayout>;

  const onLogo = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = ""; // allow re-uploading the same file after a cancel
    if (!f) return;
    if (f.size > 4 * 1024 * 1024) return toast.error("Image must be under 4MB");
    const url = await fileToDataUrl(f);
    setCrop({ target: "logo", src: url });
  };
  const onCover = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (f.size > 6 * 1024 * 1024) return toast.error("Image must be under 6MB");
    const url = await fileToDataUrl(f);
    setCrop({ target: "cover", src: url });
  };

  const handleCropConfirm = (dataUrl) => {
    if (crop.target === "logo") setR((x) => ({ ...x, logo: dataUrl }));
    else if (crop.target === "cover") setR((x) => ({ ...x, cover: dataUrl }));
    setCrop({ target: null, src: null });
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch("/restaurant", {
        name: r.name, logo: r.logo, cover: r.cover, currency: r.currency, description: r.description,
      });
      setR(data);
      toast.success("Saved");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <DashboardLayout role="owner">
      <div className="mb-8">
        <div className="text-xs uppercase tracking-[0.2em] font-bold text-brand-orange mb-2">Settings</div>
        <h1 className="font-heading font-bold text-3xl lg:text-4xl tracking-tight">Restaurant profile</h1>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <Card className="p-6 border border-border lg:col-span-2 space-y-5">
          <div>
            <Label htmlFor="rname">Restaurant name</Label>
            <Input id="rname" value={r.name} onChange={(e) => setR({ ...r, name: e.target.value })} className="mt-1.5" data-testid="profile-name-input" />
          </div>
          <div>
            <Label htmlFor="rdesc">Short description</Label>
            <Textarea id="rdesc" rows={3} value={r.description || ""} onChange={(e) => setR({ ...r, description: e.target.value })} className="mt-1.5" data-testid="profile-description-input" />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label>Display currency</Label>
              <Select value={r.currency} onValueChange={(v) => setR({ ...r, currency: v })}>
                <SelectTrigger className="mt-1.5" data-testid="profile-currency-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(CURRENCY_SYMBOLS).map(([c, s]) => (
                    <SelectItem key={c} value={c}>{s} {c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Menu URL slug</Label>
              <Input readOnly value={r.slug} className="mt-1.5 font-mono text-sm bg-muted" data-testid="profile-slug-readonly" />
            </div>
          </div>

          <Button onClick={save} disabled={saving} className="bg-brand-orange hover:bg-[#E65C00] text-white rounded-full" data-testid="profile-save-button">
            <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save changes"}
          </Button>
        </Card>

        <div className="space-y-6">
          <Card className="p-6 border border-border">
            <Label>Logo</Label>
            <div className="mt-3 flex items-center gap-4">
              <div className="w-20 h-20 rounded-xl border border-border bg-muted flex items-center justify-center overflow-hidden">
                {r.logo ? <img src={r.logo} alt="logo" className="w-full h-full object-cover" /> : <span className="text-xs text-muted-foreground">No logo</span>}
              </div>
              <div>
                <input ref={logoRef} type="file" accept="image/*" hidden onChange={onLogo} data-testid="profile-logo-file" />
                <Button variant="outline" size="sm" onClick={() => logoRef.current?.click()} data-testid="profile-logo-upload-btn"><Upload className="w-4 h-4" /> Upload</Button>
                <div className="text-xs text-muted-foreground mt-2">Max 4MB · crops to a square</div>
              </div>
            </div>
          </Card>
          <Card className="p-6 border border-border">
            <Label>Cover photo</Label>
            <div className="mt-3 rounded-xl overflow-hidden bg-muted h-28 border border-border">
              {r.cover ? <img src={r.cover} alt="cover" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-xs text-muted-foreground">No cover</div>}
            </div>
            <input ref={coverRef} type="file" accept="image/*" hidden onChange={onCover} data-testid="profile-cover-file" />
            <Button variant="outline" size="sm" className="mt-3" onClick={() => coverRef.current?.click()} data-testid="profile-cover-upload-btn"><Upload className="w-4 h-4" /> Upload cover</Button>
            <div className="text-xs text-muted-foreground mt-2">Max 6MB · crops to 16:9, shown on customer view hero.</div>
          </Card>
        </div>
      </div>

      <ImageCropModal
        open={!!crop.src}
        src={crop.src}
        onClose={() => setCrop({ target: null, src: null })}
        onConfirm={handleCropConfirm}
        aspect={crop.target === "cover" ? 16 / 9 : 1}
        cropShape={crop.target === "logo" ? "round" : "rect"}
        title={crop.target === "logo" ? "Crop logo" : "Crop cover photo"}
        description={crop.target === "logo" ? "Pan and zoom so your logo fits the square." : "Pan and zoom so the hero framing looks great on mobile."}
      />
    </DashboardLayout>
  );
}
